import { createReducer } from '@reduxjs/toolkit';
import { defaultOptions } from 'lib/hooks/ProviderSearch/useProviderSearch';
import uniqBy from 'lodash/uniqBy';
import { Nullable } from 'modules/types/common';
import analyticsService, { AnalyticsEvent } from 'services/AnalyticsService';
import { ProviderSearchOptions, ProviderSummary } from 'store/providerSearch/types';
import { oc } from 'ts-optchain';
import * as actions from './actions';

export type SearchState = Readonly<{
  isFetching: boolean;
  error: Nullable<Error>;
  data: ProviderSummary[];
  termsShown: boolean;
  count: number;
  raw: {
    data: ProviderSummary[];
    total: number;
    count: number;
  };
  filterOptions: ProviderSearchOptions;
}>;

export const searchInitialState: SearchState = {
  isFetching: false,
  error: null,
  data: [],
  termsShown: false,
  count: 0,
  raw: {
    data: [],
    total: 0,
    count: 0
  },
  filterOptions: defaultOptions
};

export const searchReducer = createReducer(searchInitialState, {
  [actions.PROVIDER_SEARCH]: state => {
    analyticsService.logEvent(AnalyticsEvent.ProviderSearchStarted);
    state.isFetching = true;
    state.error = null;
  },
  [actions.PROVIDER_SEARCH_FAIL]: (state, action) => {
    state.isFetching = false;
    state.error = action.error;
  },
  [actions.PROVIDER_SEARCH_SUCCESS]: (state, action) => {
    analyticsService.logEvent(AnalyticsEvent.ProviderSearchCompleted);

    /*
      this warrants some explanation:
      search results are technically provider-location combinations. Each "provider result" is one of it's location combinations
      when we execute a "search" we could get duplicate providers (due to multiple locations being returned in the same set)
      to deal with this, we keep track of both:
        a) the absolute total # of results we're getting back, unfiltered (contains duplicates)
        b) the same result set, but filtered for uniqueness on providerId so that a provider is only in the result set once

      However, in order to properly handle pagination, we need to keep track of each the filtered/non-filtered result sets, and the COUNT of those sets as well
      Once raw.count == raw.total, we have arrived at the "end" of the pages
    */
    const rawTotal: number = oc(action).payload.data.totalResults(0);
    const rawData: ProviderSummary[] = oc(action).payload.data.results([]);
    const data: ProviderSummary[] = uniqBy(rawData, 'providerId');

    state.isFetching = false;
    state.raw.total = rawTotal;
    state.raw.data = rawData;
    state.raw.count = rawData.length;
    state.data = data;
    state.count = data.length;
  },
  [actions.PROVIDER_SEARCH_MORE]: state => {
    state.isFetching = true;
    state.error = null;
  },
  [actions.PROVIDER_SEARCH_MORE_FAIL]: (state, action) => {
    state.isFetching = false;
    state.error = action.error;
  },
  [actions.PROVIDER_SEARCH_MORE_SUCCESS]: (state, action) => {
    const rawData: ProviderSummary[] = oc(action).payload.data.results([]);
    const allData = state.data.concat(rawData);
    const data: ProviderSummary[] = uniqBy(allData, 'providerId');

    state.isFetching = false;
    state.raw.data = state.raw.data.concat(rawData);
    state.raw.count += rawData.length;
    state.data = data;
    state.count += data.length;
  },
  [actions.PROVIDER_SEARCH_TERMS_SHOWN]: state => {
    state.termsShown = true;
  },
  [actions.RESET]: state => ({ ...searchInitialState, termsShown: state.termsShown }),
  [actions.SET_FILTER_OPTIONS]: (state, action) => {
    state.filterOptions = action.payload;
  }
});
